import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from '../contexts/LanguageContext';
import api, { OrgResponse } from '../services/api';

/**
 * Header-mounted org switcher dropdown.
 *
 * Original ticket: Phase 1 T6-B, lane (i).
 * Upgraded by Phase 3 v1.1 ``T4-ONBOARDING_SWITCHER`` (lane (g)) to:
 *
 *  - Persist the active org under the new key :const:`ACTIVE_ORG_STORAGE_KEY`
 *    (``conductor.active_org_id``). Old key ``selected_org_id`` is read at
 *    mount and silently migrated to the new key for backwards-compat with
 *    sessions that bootstrapped under T6-B.
 *  - Dispatch the ``conductor:active-org-changed`` custom event on every
 *    change (initial bootstrap + user-driven select), so siblings such
 *    as :file:`pages/OnboardingPage.tsx` and the project-list panel can
 *    refetch against the new org scope without a prop chain.
 *  - Listen to the same event so a change driven by ANOTHER component
 *    (e.g. the onboarding finalize step) updates this dropdown's
 *    selection too.
 *
 * Behavior unchanged from T6-B:
 *
 *  - On mount, calls ``GET /api/v1/orgs`` to enumerate orgs.
 *  - If the API returns 404 (prod-without-T6-B) the switcher hides itself.
 *  - When the user belongs to exactly one org, collapses to a static label.
 *
 * The visual style is intentionally minimal so the widget reads at-a-
 * glance inside both the chat header (dark) and the sidebar (light).
 * The v1.1 mockup (``full-mockup-v3-user-org.html`` .topbar) is a LIGHT
 * header — but the live chat UI still uses a dark header. We keep BOTH
 * variants so the lane (g) widget renders correctly in the live chat
 * shell without re-skinning the entire chat header in this ticket.
 */

/** New canonical storage key (Phase 3 v1.1 T4 lane (g)). */
export const ACTIVE_ORG_STORAGE_KEY = 'conductor.active_org_id';

/**
 * Legacy storage key (Phase 1 T6-B lane (i)) kept as a re-export so the
 * ChatPage import + existing test continue to compile. NEW code SHOULD
 * use :const:`ACTIVE_ORG_STORAGE_KEY` instead.
 */
export const ORG_STORAGE_KEY = ACTIVE_ORG_STORAGE_KEY;

/** Legacy storage key — read at mount, then migrated + cleared. Typed as
 *  ``string`` (not the literal ``'selected_org_id'``) so the equality
 *  check below against :const:`ACTIVE_ORG_STORAGE_KEY` does not trigger
 *  TS2367 (literal-type "no overlap" diagnostic) — the comparison is a
 *  defensive sentinel for the future case where someone changes the
 *  new key value, NOT a tautology against today's value. */
const LEGACY_ORG_STORAGE_KEY: string = 'selected_org_id';

/** Custom event name dispatched on every active-org change. */
export const ACTIVE_ORG_CHANGE_EVENT = 'conductor:active-org-changed';

/**
 * Is the given org scope the 「全部组织」 (cross-org) view?
 *
 * THIS MODULE OWNS THE REPRESENTATION, and this is the ONE place that decodes
 * it. `handleChange` maps the `<option value="">` 全部组织 entry to `null`
 * (`e.target.value || null`), and the `defaultToAllOrgs` bootstrap branch sets
 * `null` to mean the same thing. Consumers that need to know "am I cross-org
 * right now?" MUST call this rather than re-spelling the test locally: a second
 * place deciding what "unset" means is exactly the shape that mis-routed a null
 * `provider_profile` in bug-patterns #311 (see
 * `.claude/rules/sentinel-overloading.md`).
 *
 * THREE states, deliberately NOT folded into two:
 *  - `null`      → 全部组织. The sentinel proper. Returns true.
 *  - `string`    → a concrete org. Returns false.
 *  - `undefined` → the caller has NO scope information (prop omitted / legacy
 *    consumer). That is a THIRD state, not the sentinel, so it returns false
 *    and such callers degrade to their scope-unknown behaviour instead of
 *    silently inheriting "all orgs".
 *
 * Note the intended asymmetry for single-org users: the bootstrap above
 * auto-selects their lone org even under `defaultToAllOrgs` (「全部组织」 is
 * semantically identical to it), so this correctly reports false for them —
 * which is what keeps the L1 org chip out of a single-org sidebar.
 */
export function isAllOrgsScope(orgId: string | null | undefined): boolean {
  return orgId === null;
}

interface OrgSwitcherProps {
  /**
   * Called whenever the active org changes (including the initial
   * bootstrap so the parent can do its first project-list fetch with
   * the right scope). Receives ``null`` if the user has zero orgs OR
   * the endpoint is unavailable — callers should treat ``null`` as
   * "fall back to legacy non-scoped fetch".
   */
  onOrgChange?: (orgId: string | null) => void;
  /**
   * Visual variant:
   *  - ``'header'``    — dark chat header (default). Used by ChatPage.
   *  - ``'sidebar'``   — light sidebar (full width). Used by AdminShell.
   *  - ``'org-shell'`` — clean light topbar with no background tint and
   *    a tighter min-width, matching the v3 mockup (line 39
   *    ``.org-switcher-btn{background:var(--c-white)}``). Used by
   *    OrgShell topbar (T6a).
   *
   * Added in T6d (lane (i)) — the v3 mockup's topbar uses a pure white
   * background that conflicted with ``'sidebar'``'s ``#f3f4f6`` tint.
   * Existing ``'sidebar'`` consumers (AdminShell) are unaffected — only
   * the new ``'org-shell'`` value triggers the cleaner styling.
   */
  variant?: 'header' | 'sidebar' | 'org-shell';
  /**
   * When true, the bootstrap default (no valid stored selection) is the
   * "全部组织" cross-org view (``null``) instead of auto-selecting the
   * first org. The null default is NOT persisted to localStorage, so it
   * stays the default on every fresh load until the user explicitly picks
   * an org. Used by the chat surface (ChatPage header + mobile sidebar)
   * where cross-org is the preferred landing scope; org-shell pages keep
   * the first-org default because their URL requires a concrete :orgId.
   */
  defaultToAllOrgs?: boolean;
  /**
   * Reports the user's PRIMARY org id (``fetched[0].id``) once the org
   * list resolves, independent of the reconciled active selection. Unlike
   * ``onOrgChange`` (which is ``null`` under ``defaultToAllOrgs`` for the
   * cross-org chat view), this always carries a concrete first-org id when
   * the user has ≥ 1 org, or ``null`` for zero orgs / endpoint failure.
   * ChatPage uses it as the non-null fallback target for the 管理面板 link
   * so a multi-org user with no explicit selection lands on a real org
   * management page instead of bouncing through /onboarding.
   */
  onPrimaryOrgResolved?: (orgId: string | null) => void;
}

/**
 * Reads the active org id from localStorage, transparently migrating
 * the legacy key (``selected_org_id``) to the new key. Returns ``null``
 * if neither key is set.
 */
function readStoredOrgId(): string | null {
  const current = localStorage.getItem(ACTIVE_ORG_STORAGE_KEY);
  if (current) return current;
  // Migration path: if the legacy key is set but the new key isn't,
  // copy it over and clear the legacy key.
  const legacy = localStorage.getItem(LEGACY_ORG_STORAGE_KEY);
  if (legacy && ACTIVE_ORG_STORAGE_KEY !== LEGACY_ORG_STORAGE_KEY) {
    localStorage.setItem(ACTIVE_ORG_STORAGE_KEY, legacy);
    localStorage.removeItem(LEGACY_ORG_STORAGE_KEY);
    return legacy;
  }
  return null;
}

const OrgSwitcher: React.FC<OrgSwitcherProps> = ({
  onOrgChange,
  variant = 'header',
  defaultToAllOrgs = false,
  onPrimaryOrgResolved,
}) => {
  const { t } = useTranslation();
  const [orgs, setOrgs] = useState<OrgResponse[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(
    () => readStoredOrgId()
  );
  const [loaded, setLoaded] = useState(false);
  const [available, setAvailable] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Bootstrap: fetch orgs, reconcile localStorage with what the user can see.
  useEffect(() => {
    let cancelled = false;
    api
      .listMyOrgs()
      .then((fetched) => {
        if (cancelled) return;
        setOrgs(fetched);
        setAvailable(true);

        // Report the primary (first) org id independent of the reconciled
        // selection below — ChatPage uses it as the non-null 管理面板
        // fallback so a multi-org user under defaultToAllOrgs (next=null)
        // does not bounce through /onboarding.
        if (onPrimaryOrgResolved) {
          onPrimaryOrgResolved(fetched.length > 0 ? fetched[0].id : null);
        }

        // Reconcile selection: if storage points to an org the user is
        // no longer a member of (or has never been), fall back to the
        // first org. If the list is empty, clear selection entirely.
        const stored = readStoredOrgId();
        const validStored =
          stored && fetched.some((o) => o.id === stored) ? stored : null;

        // Self-heal: a stored active_org_id that is NOT among the user's
        // current memberships (deactivated org, or one they were removed
        // from) is stale and must be DELETED here. Leaving it in place
        // makes ChatPage seed `selectedOrgId` from it on mount, so the
        // "管理面板" link resolves to /orgs/<staleOrg>/overview → getOrg
        // 403/404 → /onboarding → bounce to chat. The empty-list branch
        // below only clears when the user has ZERO orgs; this covers the
        // common multi-org-with-stale-value case (e.g. defaultToAllOrgs,
        // where `next` is set to null without persisting). The valid
        // branches below re-set storage to a concrete org, so this remove
        // only persists for the cross-org default.
        if (stored && !validStored) {
          localStorage.removeItem(ACTIVE_ORG_STORAGE_KEY);
        }

        let next: string | null;
        if (validStored) {
          next = validStored;
        } else if (fetched.length === 1) {
          // Single-org user: the "全部组织" default is semantically identical
          // to that one org, so auto-select + persist it even under
          // defaultToAllOrgs. This guarantees activeOrgId is never null for a
          // single-org user, which is what drives useOrgRole's per-org role
          // resolution (canManage) and ChatPage's adminLinkTarget. Without
          // this, a single-org org-admin sees neither org-admin nav items nor
          // a working 管理面板 link. We dispatch the active-org-changed event
          // because useOrgRole seeds its activeOrgId from storage at MOUNT and
          // only updates afterwards via this event (it cannot observe the
          // async localStorage write on its own).
          next = fetched[0].id;
          localStorage.setItem(ACTIVE_ORG_STORAGE_KEY, next);
          window.dispatchEvent(
            new CustomEvent(ACTIVE_ORG_CHANGE_EVENT, { detail: { orgId: next } })
          );
        } else if (defaultToAllOrgs && fetched.length > 0) {
          // 全部组织 default (chat surface): cross-org view, NOT persisted
          // so it stays the default on every fresh load until the user
          // explicitly selects an org.
          next = null;
        } else if (fetched.length > 0) {
          next = fetched[0].id;
          localStorage.setItem(ACTIVE_ORG_STORAGE_KEY, next);
        } else {
          next = null;
          localStorage.removeItem(ACTIVE_ORG_STORAGE_KEY);
        }

        setSelectedOrgId(next);
        if (onOrgChange) {
          onOrgChange(next);
        }
      })
      .catch(() => {
        if (cancelled) return;
        // Endpoint unavailable (likely prod where T5-A is not deployed) —
        // hide the switcher; do NOT touch localStorage so a subsequent
        // dev session preserves any previously-stored selection.
        setAvailable(false);
        if (onOrgChange) {
          onOrgChange(null);
        }
        if (onPrimaryOrgResolved) {
          onPrimaryOrgResolved(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoaded(true);
        }
      });
    return () => {
      cancelled = true;
    };
    // onOrgChange intentionally NOT in deps — we only want to refetch on
    // mount, not whenever the parent rerenders with a new callback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for cross-component active-org changes (e.g. OnboardingPage
  // finalize step calls dispatchEvent before navigating). Refresh the
  // org list so the dropdown immediately reflects new memberships, and
  // re-sync local selection state.
  useEffect(() => {
    const handler = (event: Event): void => {
      const detail = (event as CustomEvent<{ orgId?: string | null }>).detail;
      const next = detail?.orgId ?? readStoredOrgId();
      // Re-fetch the org list — the user may have just joined a new org.
      api
        .listMyOrgs()
        .then((fetched) => {
          setOrgs(fetched);
          setAvailable(true);
          // Only update selection if the new id is actually a member.
          if (next && fetched.some((o) => o.id === next)) {
            setSelectedOrgId(next);
          } else if (fetched.length === 1) {
            // Single-org user — mirror the bootstrap branch: auto-select +
            // persist the lone org even under defaultToAllOrgs so a later
            // mount reads it. We do NOT re-dispatch the event here: this
            // handler runs IN RESPONSE to the active-org-changed event, which
            // useOrgRole already received, so re-dispatching would loop.
            localStorage.setItem(ACTIVE_ORG_STORAGE_KEY, fetched[0].id);
            setSelectedOrgId(fetched[0].id);
          } else if (defaultToAllOrgs && fetched.length > 0) {
            // 全部组织 default (chat surface) — fall back to cross-org
            // rather than auto-selecting the first org.
            setSelectedOrgId(null);
          } else if (fetched.length > 0) {
            setSelectedOrgId(fetched[0].id);
          }
        })
        .catch(() => {
          // Endpoint unreachable — keep current state, don't clobber.
        });
    };
    window.addEventListener(ACTIVE_ORG_CHANGE_EVENT, handler);
    return () => window.removeEventListener(ACTIVE_ORG_CHANGE_EVENT, handler);
    // defaultToAllOrgs is a static prop (never changes after mount) — the
    // listener should attach once, so it is intentionally omitted from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const next = e.target.value || null;
    setSelectedOrgId(next);
    if (next) {
      localStorage.setItem(ACTIVE_ORG_STORAGE_KEY, next);
    } else {
      localStorage.removeItem(ACTIVE_ORG_STORAGE_KEY);
    }
    if (onOrgChange) {
      onOrgChange(next);
    }
    // Dispatch the event so siblings refetch. The handler above guards
    // against self-update loops by re-reading from storage (idempotent
    // when the new id is the one we just wrote).
    window.dispatchEvent(
      new CustomEvent(ACTIVE_ORG_CHANGE_EVENT, {
        detail: { orgId: next },
      })
    );

    // Sync the URL :orgId segment so org-scoped pages (OrgMembers,
    // OrgProjects, etc.) refetch under the new org. Without this,
    // OrgShell's URL→storage sync would overwrite our just-written
    // storage value with the stale URL value on the next render —
    // see bug-patterns entry "Storage-as-source-of-truth + URL-as-
    // source-of-truth feedback loop".
    if (next) {
      const orgPathMatch = location.pathname.match(/^\/orgs\/[^/]+(\/.*)?$/);
      if (orgPathMatch) {
        // On an /orgs/:orgId/<rest> route — swap the :orgId segment.
        const rest = orgPathMatch[1] || '/overview';
        navigate(`/orgs/${next}${rest}`);
      } else if (
        location.pathname.startsWith('/admin') ||
        location.pathname === '/profile'
      ) {
        // On /admin/* or /profile — land on the new org's overview.
        // (Root `/` was previously included but is dead code: ChatPage
        // mounts at /c/:conversationId, not /, so switching org on the
        // chat surface should NOT force-navigate to a new URL — the
        // event dispatch above is sufficient. See M4 (b) lane.)
        navigate(`/orgs/${next}/overview`);
      }
      // Otherwise (e.g. ChatPage, OnboardingPage) — do not redirect; the
      // event dispatch already notifies sibling consumers.
    }
  };

  // Hide entirely until we know whether the endpoint is reachable AND
  // the user belongs to at least one org. Avoids flashing an empty
  // dropdown.
  if (!loaded || !available || orgs.length === 0) {
    return null;
  }

  // T6d (lane (i)): style picker for the 3-variant matrix. Adding a
  // single map here keeps existing 'header' / 'sidebar' consumers
  // untouched while introducing 'org-shell' without inline hex (the
  // CSS variable lookups stay scoped to OrgShell.tsx; the inline
  // styles below pick from a frozen palette).
  const labelStyle =
    variant === 'sidebar'
      ? styles.sidebarLabel
      : variant === 'org-shell'
        ? styles.orgShellLabel
        : styles.label;
  const selectStyle =
    variant === 'sidebar'
      ? styles.sidebarSelect
      : variant === 'org-shell'
        ? styles.orgShellSelect
        : styles.select;

  // Single-org case: render as a static label, not a dropdown.
  if (orgs.length === 1) {
    const only = orgs[0];
    return (
      <span
        style={labelStyle}
        aria-label={t('orgNav.aria.current_org')}
        data-testid="org-switcher-static"
        data-variant={variant}
      >
        {only.display_name}
      </span>
    );
  }

  return (
    <select
      value={selectedOrgId || ''}
      onChange={handleChange}
      style={selectStyle}
      aria-label={t('orgNav.aria.select_org')}
      data-testid="org-switcher-select"
      data-variant={variant}
    >
      {/* 全部组织 sentinel (value="") — handleChange maps "" -> null ->
          selectedOrgId=null. On the chat header (the platform-admin god-
          mode surface) this restores the cross-org project view after a
          per-org narrowing. Gated to the 'header' variant only: the
          'sidebar'/'org-shell' variants drive org-scoped pages whose URL
          requires a concrete :orgId, so a null selection is meaningless
          there. */}
      {(variant === 'header' || defaultToAllOrgs) && (
        <option value="">{t('orgNav.nav.all_orgs')}</option>
      )}
      {orgs.map((org) => (
        <option key={org.id} value={org.id}>
          {org.display_name}
        </option>
      ))}
    </select>
  );
};

const styles: Record<string, React.CSSProperties> = {
  select: {
    padding: '6px 12px',
    fontSize: '13px',
    color: '#ffffff',
    backgroundColor: '#2d2d4a',
    border: '1px solid #4b5563',
    borderRadius: '4px',
    cursor: 'pointer',
    outline: 'none',
    // minHeight removed + minWidth 120->140 to visually match
    // ProjectSelector.tsx::styles.select, which sits beside this box in the
    // chat header. The 44px touch-target floor is intentionally dropped on
    // the desktop-primary header variant (ProjectSelector already omits it);
    // mobile uses the sidebar variant, which keeps minHeight: 44.
    minWidth: '140px',
    marginRight: '8px',
    appearance: 'auto' as React.CSSProperties['appearance'],
  },
  sidebarSelect: {
    padding: '6px 12px',
    fontSize: '13px',
    color: '#1f2937',
    backgroundColor: '#f3f4f6',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    cursor: 'pointer',
    outline: 'none',
    width: '100%',
    minHeight: 44,
    appearance: 'auto' as React.CSSProperties['appearance'],
  },
  label: {
    padding: '6px 12px',
    fontSize: '13px',
    color: '#ffffff',
    backgroundColor: 'transparent',
    border: '1px solid transparent',
    marginRight: '8px',
  },
  sidebarLabel: {
    padding: '6px 12px',
    fontSize: '13px',
    color: '#1f2937',
    fontWeight: 500,
  },
  // T6d (lane (i)) — OrgShell topbar variant. Matches mockup line 39
  // .org-switcher-btn (background: var(--c-white), 1px border, min-
  // height 36px, font-size 13px, gap 6px). Width is intrinsic (no
  // 100% sidebar fill, no 120px header floor) so the topbar's flex
  // spacer can squeeze the widget on narrow viewports without
  // overflowing.
  orgShellSelect: {
    padding: '6px 10px',
    fontSize: '13px',
    color: '#1f2937',
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    cursor: 'pointer',
    outline: 'none',
    minHeight: 36,
    appearance: 'auto' as React.CSSProperties['appearance'],
  },
  orgShellLabel: {
    padding: '6px 10px',
    fontSize: '13px',
    color: '#1f2937',
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    minHeight: 36,
    display: 'inline-flex',
    alignItems: 'center',
    fontWeight: 500,
  },
};

export default OrgSwitcher;
